import { useCallback, useMemo } from "react";

import ACL_RELATIONSHIPS from "acl-relationships";
import usePageTitle from "hooks/usePageTitle";
import { getNextQueryParams } from "components/helpers";
import useAuthorized from "hooks/useAuthorized";
import BaseTableV3 from "components/BaseTableV3/BaseTableV3";
import { CallsTableBulkActions } from "components/CallsTableV3/CallsTableBulkActions";
import CampaignTargetCallService from "services/CampaignTargetCall";
import { useCallsTable } from "hooks/useCallsTable";
import { omitByNil } from "helpers/tables";

export const CALLS_TABLE_CONSTANTS = {
  tableName: "calls",
  className: "CampaignTargetCall",
  exportTableName: "CampaignTargetCallsTable",
  header: "Calls",
  headerIcon: "call",
};

const CallsTableV3 = ({ campaignId, entityId, contactId }) => {
  const enableTags = useAuthorized(
    [
      ACL_RELATIONSHIPS.callTags.create,
      ACL_RELATIONSHIPS.callTags.read,
      ACL_RELATIONSHIPS.callTags.delete,
    ],
    { partialMatch: true }
  );
  const isStandalone = !campaignId && !entityId && !contactId;
  usePageTitle(isStandalone ? CALLS_TABLE_CONSTANTS.header : null);

  const defaultQueryParams = useMemo(() => {
    return omitByNil({
      campaign_id: campaignId,
      entity_id: entityId,
      contact_id: contactId,
    });
  }, [campaignId, entityId, contactId]);

  const { filters, columns, queryKey, invalidateData } = useCallsTable({
    campaignId: campaignId,
    entityId: entityId,
    contactId: contactId,
  });

  const fetchCalls = useCallback(
    async queryParams => {
      try {
        const nextQueryParams = getNextQueryParams(queryParams, columns);
        const response = await CampaignTargetCallService.getTable({
          ...nextQueryParams,
          ...defaultQueryParams,
        });

        return response;
      } catch (error) {
        console.error("Error fetching calls:", error);
        throw error;
      }
    },
    [columns, defaultQueryParams]
  );

  const actionButtons = useCallback(
    ({ selectedIds, selectedData, uncheckAll }) => (
      <CallsTableBulkActions
        selectedIds={selectedIds}
        selectedData={selectedData}
        uncheckAll={uncheckAll}
        invalidateData={invalidateData}
      />
    ),
    [invalidateData]
  );

  return (
    <BaseTableV3
      actionButtons={actionButtons}
      columns={columns}
      filters={filters}
      headerIcon={CALLS_TABLE_CONSTANTS.headerIcon}
      header={CALLS_TABLE_CONSTANTS.header}
      tableName={CALLS_TABLE_CONSTANTS.tableName}
      queryKey={queryKey}
      className={CALLS_TABLE_CONSTANTS.className}
      exportTableName={CALLS_TABLE_CONSTANTS.exportTableName}
      tagsDropdownParams={{ visible: enableTags }}
      enableSearch
      enableSettings
      fetchData={fetchCalls}
      invalidateData={invalidateData}
    />
  );
};

export default CallsTableV3;
